// Question visibility and grouping for the package builder.
//
// These rules are applied for DISPLAY ONLY. CASS evaluates the same conditions
// when pricing, and returns 422 naming any required-and-visible answer we failed
// to collect — so the server is always the arbiter and divergence is loud.

export const truthy = (v) => v === true || v === 1 || v === "yes" || v === "true";

function testCondition(c, answers) {
  const a = answers[c.factor];
  const v = c.value;
  switch (c.op) {
    case "yes": return truthy(a);
    case "no": return !truthy(a);
    case "ne": return String(a ?? "") !== String(v ?? "");
    case "gt": return Number(a) > Number(v);
    case "lt": return Number(a) < Number(v);
    case "gte": return Number(a) >= Number(v);
    case "lte": return Number(a) <= Number(v);
    case "contains": return String(a ?? "").toLowerCase().includes(String(v ?? "").toLowerCase());
    case "eq":
    default: return String(a ?? "") === String(v ?? "");
  }
}

export function isVisible(q, answers) {
  const cond = q.condition;
  if (!cond || !Array.isArray(cond.conditions) || cond.conditions.length === 0) return true;
  const results = cond.conditions.map((c) => testCondition(c, answers));
  return cond.match === "all" ? results.every(Boolean) : results.some(Boolean);
}

export const hasValue = (v) => v !== undefined && v !== null && v !== "";

// Answers are one flat object across all services, so a key that appears in more
// than one service is asked once — the first occurrence in catalogue order wins.
export function buildSections(services, selectedIds, answers, hideKeys = []) {
  const seen = new Set();
  return (services || [])
    .filter((s) => selectedIds.includes(s.service_id))
    .map((s) => {
      const questions = [...(s.questions || [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter((q) => {
          if (seen.has(q.key)) return false;
          seen.add(q.key);
          return true;
        })
        .filter((q) => isVisible(q, answers));
      const groups = [];
      // Questions we answer on the visitor's behalf stay in the payload but aren't
      // shown — there's no point asking something we already know.
      questions.filter((q) => !hideKeys.includes(q.key)).forEach((q) => {
        const name = q.group || "";
        let g = groups.find((x) => x.name === name);
        if (!g) { g = { name, questions: [] }; groups.push(g); }
        g.questions.push(q);
      });
      return { service: s, questions, groups };
    });
}

// Never send an answer for a question we're currently hiding.
export function answersToSend(sections, answers) {
  const out = {};
  sections.forEach((sec) => sec.questions.forEach((q) => {
    if (hasValue(answers[q.key])) out[q.key] = answers[q.key];
  }));
  return out;
}

export function missingRequired(sections, answers) {
  const out = [];
  sections.forEach((sec) => sec.questions.forEach((q) => {
    if (q.required && !hasValue(answers[q.key])) {
      out.push({ service_id: sec.service.service_id, key: q.key, label: q.label });
    }
  }));
  return out;
}

export const svcMode = (s) => s?.display?.mode || "optional";
// Answers pre-selected for a fresh visitor; they can still change any of them.
export function defaultAnswers(services) {
  const out = {};
  (services || []).forEach((s) => (s.questions || []).forEach((q) => {
    if (q.default !== undefined && q.default !== "" && out[q.key] === undefined) out[q.key] = q.default;
  }));
  return out;
}
// Policy flag from the admin: CASS sells this only alongside bookkeeping.
export const requiresBookkeeping = (s) => !!s?.display?.requires_bookkeeping;
export const requiresBookkeepingIds = (services) =>
  (services || []).filter(requiresBookkeeping).map((s) => s.service_id);
export const alwaysIds = (services) => (services || []).filter((s) => svcMode(s) === "always").map((s) => s.service_id);
// Policy flag from the admin: this work doesn't take anyone on as a client, so when
// it's the whole basket the mandatory onboarding fee is left out.
export const skipsOnboarding = (s) => !!s?.display?.no_onboarding;
export const skipsOnboardingIds = (services) =>
  (services || []).filter(skipsOnboarding).map((s) => s.service_id);
export const preselectedIds = (services) => (services || []).filter((s) => svcMode(s) === "preselected").map((s) => s.service_id);
// Listed mandatory services are shown to the visitor as included as standard.
export const listedAlways = (services) => (services || []).filter((s) => svcMode(s) === "always" && s.display?.listed !== false);

export function groupByCategory(services) {
  const cats = [];
  (services || []).forEach((s) => {
    let c = cats.find((x) => x.name === (s.category || "Other"));
    if (!c) { c = { name: s.category || "Other", services: [] }; cats.push(c); }
    c.services.push(s);
  });
  return cats;
}

export const money = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
